/**
 * iter-170ag — Workspace: hub for the 6 catalog features that don't
 * have a dedicated route yet (Smart alerts, Read-only accountant
 * link, Advanced exports, Basic API, Audit log, Roles & permissions).
 *
 * Each section is wrapped in a ``LockedSection`` so:
 *   • Users on plans WITHOUT the feature see the upsell card with
 *     concrete plan/addon/pack recommendations.
 *   • Users on plans WITH the feature see an honest "En desarrollo"
 *     stub with a "Avísame" CTA that posts a support ticket — same
 *     pattern as the original Scenarios page so we don't ship fake
 *     UI for features that aren't built yet.
 *
 * Why centralize: spinning up 6 separate routes for stub pages would
 * pollute the sidebar. One Workspace page keeps the upsell visible
 * (each section has its own LockedSection upsell card) without the
 * cost of 6 empty pages.
 */
import { useState } from "react";
import { Layout } from "../../components/Layout";
import { PageHeader } from "../../components/PageHeader";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { LockedSection } from "../../components/LockedSection";
import { useEntitlements } from "../../hooks/useEntitlements";
import { AuditLogViewer } from "../../components/AuditLogViewer";
import { ApiKeysManager } from "../../components/ApiKeysManager";
import { AdvancedExportsCard } from "../../components/AdvancedExportsCard";
import { AccountantLinkCard } from "../../components/AccountantLinkCard";
import { SmartAlertsCard } from "../../components/SmartAlertsCard";
import { RolesPermissionsCard } from "../../components/RolesPermissionsCard";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Bell, Eye, Download, KeyRound, History, Users, Hourglass, CheckCircle2 } from "lucide-react";

// Coming-soon stub — same honest pattern used by Scenarios. Reused
// across all 6 sections so entitled users get a consistent "we're
// building this, ping us when ready" experience instead of fake UI.
function ComingSoonStub({ featureKey, lang, Icon }) {
  const [done, setDone] = useState(false);
  const notify = async () => {
    const body = tx(
      lang,
      `Hi, I'm interested in the "${featureKey}" feature. Please let me know when it ships.`,
      `Hola, me interesa la funcionalidad "${featureKey}". Por favor avísenme cuando esté disponible.`,
      `Olá, tenho interesse na funcionalidade "${featureKey}". Avisem quando estiver disponível.`,
    );
    try {
      await api.post("/support/chat/send", { text: body });
      setDone(true);
      toast.success(tx(lang, "We'll ping you", "Te avisaremos", "Avisaremos"));
    } catch {
      toast.error(tx(lang, "Send failed", "Error al enviar", "Erro ao enviar"));
    }
  };
  return (
    <Card
      className="p-5 rounded-2xl border-amber-200 bg-amber-50/30"
      data-testid={`workspace-stub-${featureKey}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Hourglass className="h-3.5 w-3.5 text-amber-700" />
            <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700">
              {tx(lang, "In development", "En desarrollo", "Em desenvolvimento")}
            </span>
          </div>
          <p className="text-xs text-slate-700 leading-snug">
            {tx(lang,
              "Your plan includes this. We're building it now — opt in to be notified when it ships.",
              "Tu plan la incluye. La estamos construyendo — pide aviso cuando esté lista.",
              "Seu plano inclui. Estamos construindo — peça aviso quando ficar pronto.")}
          </p>
          <div className="mt-3">
            {done ? (
              <p
                className="text-xs text-emerald-700 flex items-center gap-1"
                data-testid={`workspace-notified-${featureKey}`}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                {tx(lang, "We'll ping you", "Te avisaremos", "Avisaremos")}
              </p>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={notify}
                data-testid={`workspace-notify-${featureKey}`}
                className="gap-1.5 rounded-full"
              >
                <Bell className="h-3.5 w-3.5" />
                {tx(lang, "Notify me", "Avísame", "Avise-me")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

const SECTIONS = [
  {
    featureKey: "smart_alerts",
    icon: Bell,
    // iter-170am — Short, flat labels per user request (1b).
    titleEs: "Alertas",
    titleEn: "Alerts",
    titlePt: "Alertas",
    bullets: [
      { es: "Avisos cuando tu caja baja de un umbral", en: "Alert when cash drops below a threshold", pt: "Alertas quando o caixa cai" },
      { es: "Detecta gastos atípicos automáticamente", en: "Auto-detect unusual expenses", pt: "Detecta despesas atípicas" },
      { es: "Notificación push o por bot", en: "Push or bot notifications", pt: "Notificação push ou bot" },
    ],
  },
  {
    featureKey: "accountant_readonly",
    icon: Eye,
    titleEs: "Link lectura",
    titleEn: "Read link",
    titlePt: "Link leitura",
    bullets: [
      { es: "Comparte tus reportes sin dar acceso de edición", en: "Share reports without granting edit access", pt: "Compartilhe relatórios sem dar edição" },
      { es: "Token revocable cuando quieras", en: "Token revocable anytime", pt: "Token revogável a qualquer hora" },
      { es: "Vista limitada a reportes y movimientos", en: "Scoped to reports & movements only", pt: "Escopo limitado a relatórios e movimentos" },
    ],
  },
  {
    featureKey: "advanced_exports",
    icon: Download,
    titleEs: "Exportaciones",
    titleEn: "Exports",
    titlePt: "Exportações",
    bullets: [
      { es: "CSV/Excel con filtros personalizados", en: "CSV/Excel with custom filters", pt: "CSV/Excel com filtros customizados" },
      { es: "Exportación programada por email", en: "Scheduled email exports", pt: "Exportações agendadas por email" },
      { es: "Compatible con Power BI / Looker / Sheets", en: "Compatible with Power BI / Looker / Sheets", pt: "Compatível com Power BI / Looker / Sheets" },
    ],
  },
  {
    featureKey: "basic_api",
    icon: KeyRound,
    titleEs: "API Key",
    titleEn: "API Key",
    titlePt: "API Key",
    bullets: [
      { es: "Acceso programático a tus movimientos y reportes", en: "Programmatic access to movements & reports", pt: "Acesso programático a movimentos e relatórios" },
      { es: "API key revocable desde el perfil", en: "API key revocable from profile", pt: "API key revogável no perfil" },
      { es: "Rate-limit generoso para integraciones personales", en: "Generous rate-limit for personal integrations", pt: "Rate-limit generoso para integrações pessoais" },
    ],
  },
  {
    featureKey: "audit_log",
    icon: History,
    titleEs: "Auditoría",
    titleEn: "Audit",
    titlePt: "Auditoria",
    bullets: [
      { es: "Quién modificó qué y cuándo", en: "Who changed what & when", pt: "Quem alterou o quê e quando" },
      { es: "Retención extendida según tu plan", en: "Extended retention per your plan", pt: "Retenção estendida conforme plano" },
      { es: "Exportable para cumplimiento contable", en: "Exportable for accounting compliance", pt: "Exportável para compliance contábil" },
    ],
  },
  {
    featureKey: "roles_permissions",
    icon: Users,
    titleEs: "Usuarios",
    titleEn: "Users",
    titlePt: "Usuários",
    bullets: [
      { es: "Invita miembros con roles (admin / editor / lectura)", en: "Invite members with roles (admin / editor / viewer)", pt: "Convide membros com funções" },
      { es: "Permisos granulares por módulo", en: "Granular per-module permissions", pt: "Permissões granulares por módulo" },
      { es: "Auditoría por usuario sobre cambios", en: "Per-user audit on changes", pt: "Auditoria por usuário" },
    ],
  },
];

export default function Workspace() {
  const { lang } = useLang();
  const { has, isLoading: entLoading } = useEntitlements();

  // iter-170ak — Render-on-navigate. We track which tabs the user has
  // ever opened in this session; only those mount their children. This
  // keeps the initial load light (just the first tab's hooks fire)
  // and avoids re-running expensive queries (api-keys, audit-log,
  // smart-alerts events) for tabs the user never visits.
  const initial = SECTIONS[0]?.featureKey;
  const [active, setActive] = useState(initial);
  const [mounted, setMounted] = useState(() => new Set([initial]));

  const onTabChange = (key) => {
    setActive(key);
    if (!mounted.has(key)) {
      const next = new Set(mounted);
      next.add(key);
      setMounted(next);
    }
  };

  const renderContent = (s) => {
    // Don't even render the LockedSection (which would fire
    // useFeatureRecommendation) for tabs the user hasn't opened yet.
    if (!mounted.has(s.featureKey)) return null;
    return (
      <LockedSection
        featureKey={s.featureKey}
        titleEs={s.titleEs}
        titleEn={s.titleEn}
        titlePt={s.titlePt}
        bullets={s.bullets}
      >
        {s.featureKey === "audit_log" ? <AuditLogViewer />
          : s.featureKey === "basic_api" ? <ApiKeysManager />
          : s.featureKey === "advanced_exports" ? <AdvancedExportsCard />
          : s.featureKey === "accountant_readonly" ? <AccountantLinkCard />
          : s.featureKey === "smart_alerts" ? <SmartAlertsCard />
          : s.featureKey === "roles_permissions" ? <RolesPermissionsCard />
          : <ComingSoonStub featureKey={s.featureKey} lang={lang} Icon={s.icon} />}
      </LockedSection>
    );
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl" data-testid="workspace-page">
        <PageHeader
          title={tx(lang, "Workspace", "Espacio de trabajo", "Espaço de trabalho")}
          subtitle={tx(lang,
            "Team, governance and integrations features — gated by your plan.",
            "Equipo, gobierno e integraciones — gateado por tu plan.",
            "Equipe, governança e integrações — limitado pelo seu plano.")}
        />
        <Tabs value={active} onValueChange={onTabChange} className="w-full">
          {/* Horizontal scroll on narrow viewports so the 6 tabs don't
              collapse into a tiny target. Each tab shows the section
              icon + label + a small lock badge when not entitled. */}
          <TabsList
            className="w-full justify-start gap-1 overflow-x-auto h-auto p-1 bg-slate-100/70"
            data-testid="workspace-tabs-list"
          >
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const locked = !entLoading && !has(s.featureKey);
              return (
                <TabsTrigger
                  key={s.featureKey}
                  value={s.featureKey}
                  className="gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm whitespace-nowrap"
                  data-testid={`workspace-tab-${s.featureKey}`}
                >
                  <Icon className={`h-3.5 w-3.5 ${locked ? "text-slate-400" : "text-slate-700"}`} />
                  <span className="text-xs">
                    {tx(lang, s.titleEn, s.titleEs, s.titlePt)}
                  </span>
                  {locked && (
                    <span className="ml-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                      PRO
                    </span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {SECTIONS.map((s) => (
            <TabsContent
              key={s.featureKey}
              value={s.featureKey}
              className="mt-5"
              forceMount={mounted.has(s.featureKey) ? true : undefined}
              hidden={active !== s.featureKey}
              data-testid={`workspace-content-${s.featureKey}`}
            >
              {renderContent(s)}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </Layout>
  );
}
